import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    linkedSignal,
    OnDestroy,
    output,
    viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { uniqueId } from 'lodash-es';
import { BehaviorSubject, of, Subject, timer } from 'rxjs';
import { debounce, debounceTime, filter, groupBy, mergeMap } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import { ActionItem, ActionItemModifier, ActionItemStyle, ShActionItemComponent } from '../../../../elements/components/action-item/action-item.component';
import { ShClickOutsideDirective } from '../../../../elements/directives/click-outside/click-outside.directive';
import { ShDroppanelComponent } from '../../../../elements/components/droppanel/droppanel.component';
import { ShFacetComponent } from '../../../../elements/components/facet/facet.component';
import { ShFacet, ShSelectedFacetValue } from '../../../../elements/components/facet/entities/facet.entity';
import { ShIconComponent } from '../../../../elements/components/icon/icon.component';
import { FieldType } from '../../../../core/field-definitions/entities/field-definition.entity';
import { ColumnDateFilterComponent } from './column-date-filter/column-date-filter.component';

const TEXT_FILTERABLE_TYPES: FieldType[] = [FieldType.TextField, FieldType.SimpleField];
const DEBOUNCE_MS = 450;

/**
 * One labelled free-text input, for a column that renders more than one field and so cannot be filtered
 * through a single unlabelled box — edepot's "Naam en serie" is the only such column today.
 */
export interface ShColumnFilterField {
    /** The metadata field this input filters on; also the key the container writes into the column-filter state. */
    field: string;
    /** Shown above the input, and reused as the field's active-filter pill label. */
    label: string;
    /** The value currently committed for this field. */
    value: string;
}

@Component({
    standalone: true,
    selector: 'sh-column-filter',
    templateUrl: './column-filter.component.html',
    styleUrls: ['./column-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslateModule,
        ShActionItemComponent,
        ShClickOutsideDirective,
        ShDroppanelComponent,
        ShFacetComponent,
        ShIconComponent,
        ColumnDateFilterComponent
    ]
})
export class ColumnFilterComponent implements OnDestroy {
    private readonly translateService: SharedLanguageService = inject(SharedLanguageService);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    field = input.required<string>();
    fieldType = input<FieldType | undefined>();
    value = input<string>('');

    /**
     * The labelled free-text inputs to render instead of the single unlabelled one. Non-empty only for a
     * column that filters on more than one field, and it then replaces every other control: such a column
     * has no field type of its own to switch on.
     *
     * Hand this a reference that only changes when a value does — the editable buffers below reset with it,
     * so a fresh array on every change-detection pass would wipe what the user is typing.
     */
    textFields = input<readonly ShColumnFilterField[]>([]);

    /** The live facet buckets for this column's field (same values the side-panel facet shows), loaded when the dropdown opens. */
    enumFacet = input<ShFacet | null>(null);

    /** The values currently selected for this field in the shared facet state — drives the active indicator without needing the buckets loaded. */
    selectedEnumValues = input<string[]>([]);

    valueChanged = output<string>();

    /** The multi-field counterpart of {@link valueChanged}: says which of the labelled inputs changed. */
    fieldValueChanged = output<{ field: string; value: string }>();

    /** Fires when an enum dropdown opens, so the container can lazily load its facet buckets. */
    opened = output<void>();

    /** Re-emits a facet value toggled in the enum dropdown, for the container to add/remove from the shared facet state. */
    facetValueSelected = output<ShSelectedFacetValue>();

    /** Fires when the enum dropdown's reset is clicked, so the container can clear all of this field's selected facet values. */
    enumCleared = output<void>();

    /** Editable buffer for the text input, seeded from `value` and reset whenever `value` changes externally (e.g. clear-all). */
    readonly inputValue = linkedSignal<string>(() => this.value());

    /** The committed date-range value (a `[start TO end]` query string), tracked separately so the active indicator updates on apply/clear; re-syncs when `value` is reset externally. */
    readonly dateValue = linkedSignal<string>(() => this.value());

    /**
     * Editable buffers for the labelled inputs, keyed by field. Each one resets only when **its own**
     * committed value changes, which is what the single input's `linkedSignal(() => this.value())` gets for
     * free from string equality. Resetting the whole record whenever the array changed would blank a
     * half-typed sibling every time the other input's debounce committed.
     */
    readonly fieldInputValues = linkedSignal<readonly ShColumnFilterField[], Record<string, string>>({
        source: this.textFields,
        computation: (textFields, previous) =>
            Object.fromEntries(
                textFields.map(textField => {
                    if (!previous) {
                        return [textField.field, textField.value];
                    }

                    const wasCommitted: string | undefined = previous.source.find(
                        earlier => earlier.field === textField.field
                    )?.value;

                    // Unchanged committed value: whatever the user has typed in this input stands. A field
                    // seen for the first time has no `wasCommitted`, so it seeds rather than reads `previous`.
                    return [
                        textField.field,
                        wasCommitted === textField.value ? previous.value[textField.field] : textField.value
                    ];
                })
            )
    });

    readonly isOpen$ = new BehaviorSubject<boolean>(false);

    readonly isMultiText = computed(() => this.textFields().length > 0);

    readonly isEnum = computed(() => this.fieldType() === FieldType.EnumField);

    readonly isBoolean = computed(() => this.fieldType() === FieldType.BooleanField);

    readonly isDate = computed(() => this.fieldType() === FieldType.DateField);

    /** Both enum and boolean columns share the same facet dropdown, driven by the container's shared facet state. */
    readonly isFacet = computed(() => this.isEnum() || this.isBoolean());

    readonly isTextFilterable = computed(() => {
        const fieldType = this.fieldType();
        return !!fieldType && TEXT_FILTERABLE_TYPES.includes(fieldType);
    });

    readonly isFilterable = computed(
        () => this.isMultiText() || this.isTextFilterable() || this.isFacet() || this.isDate()
    );

    readonly isActive = computed(() => {
        if (this.isMultiText()) {
            const values: Record<string, string> = this.fieldInputValues();
            return this.textFields().some(textField => (values[textField.field] ?? '').length > 0);
        }
        if (this.isFacet()) {
            return this.selectedEnumValues().length > 0;
        }
        if (this.isDate()) {
            return this.dateValue().length > 0;
        }
        return this.inputValue().length > 0;
    });

    readonly toggleAction = computed<ActionItem>(() => ({
        label: this.translateService.translate('column-filter.toggle'),
        icon: 'magnifier',
        style: this.isActive() ? ActionItemStyle.PureSquareIconButtonLight : ActionItemStyle.PureSquareIconButton,
        modifiers: [ActionItemModifier.Small],
        eventListener: () => this.toggleOpen()
    }));

    /** Both variants' first typeable field, so the single input and the first labelled one share the focus path. */
    private readonly textInput = viewChild<ElementRef<HTMLInputElement>>('textInput');

    private readonly dateFilter = viewChild(ColumnDateFilterComponent);

    private readonly valueChanges$ = new Subject<string>();

    private readonly fieldValueChanges$ = new Subject<{ field: string; value: string }>();

    /** Unique per instance, so two multi-field filters on one page never share a label's `for` target. */
    private readonly instanceId: string = uniqueId();

    constructor() {
        this.valueChanges$
            .pipe(
                debounceTime(DEBOUNCE_MS),
                filter(value => value !== this.value()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(value => this.valueChanged.emit(value));

        // Debounced per field rather than across all of them: one shared window would let a keystroke in the
        // second input swallow the first input's pending value, and that filter would then never apply.
        this.fieldValueChanges$
            .pipe(
                groupBy(change => change.field),
                mergeMap(changesForField =>
                    changesForField.pipe(
                        // A cleared field applies at once: `of` emits while `debounce` is still subscribing,
                        // so the window is zero-length. (`EMPTY` would not do — `debounce` releases the
                        // value on the notifier's *next*, never on its complete.) Going through the same
                        // stream rather than around it is what cancels a keystroke still pending, so the
                        // clear cannot be undone a moment later, and it emits once rather than twice.
                        debounce(change => (change.value === '' ? of(0) : timer(DEBOUNCE_MS))),
                        filter(change => change.value !== this.committedFieldValue(change.field))
                    )
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(change => this.fieldValueChanged.emit(change));

        this.isOpen$
            .pipe(
                filter(isOpen => isOpen),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => this.focusFirstField());
    }

    ngOnDestroy(): void {
        this.isOpen$.complete();
    }

    inputId(field: string): string {
        return `sh-column-filter-${this.instanceId}-${field}`;
    }

    fieldInputValue(field: string): string {
        return this.fieldInputValues()[field] ?? '';
    }

    onFieldInput(field: string, value: string): void {
        this.fieldInputValues.update(values => ({ ...values, [field]: value }));
        this.fieldValueChanges$.next({ field, value });
    }

    /** Clears one labelled input via its inline clear icon; the stream applies a blank without waiting. */
    clearFieldInput(field: string): void {
        this.onFieldInput(field, '');
    }

    onInput(value: string): void {
        this.inputValue.set(value);
        this.valueChanges$.next(value);
    }

    /** Clears the text input and drops the filter immediately (no debounce), via the inline clear icon. */
    clearInput(): void {
        this.inputValue.set('');
        this.valueChanged.emit('');
    }

    /** A date range is already a well-formed query string committed via the popover's Apply/Clear, so it is emitted immediately without debouncing. */
    onDateRangeChanged(range: string): void {
        this.dateValue.set(range);
        this.valueChanged.emit(range);
    }

    close(): void {
        this.isOpen$.next(false);
    }

    private committedFieldValue(field: string): string {
        return this.textFields().find(textField => textField.field === field)?.value ?? '';
    }

    private toggleOpen(): void {
        const willOpen: boolean = !this.isOpen$.value;
        this.isOpen$.next(willOpen);

        if (willOpen && this.isFacet()) {
            this.opened.emit();
        }
    }

    /**
     * Puts the caret in the panel's first typeable field on open, so filtering takes one click instead of
     * two. The facet variant has nothing to type into, hence no branch for it. Deferred a tick because
     * `sh-droppanel` keeps its content mounted and only toggles `display: none` — an unrendered field
     * cannot take focus.
     */
    private focusFirstField(): void {
        setTimeout(() => {
            this.textInput()?.nativeElement.focus();
            this.dateFilter()?.focusFromField();
        });
    }
}
